import { useEffect, useState } from 'react';
import {
  ActionIcon, Avatar, Box, Button, Center, Group, Loader, Menu, Modal, Pagination, Select, SimpleGrid, Stack, Table, Text, Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { IconCheck, IconChevronDown, IconSearch } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { SectionTitle } from './TitleSection';
import { InlineNotice } from '../../components/InlineNotice';
import { api, bearer } from '../../api/client';
import {
  adminActionErrorText, dismissAccessRequest, dismissAllAccessRequests, grantAccessRequest, grantAllAccessRequests, listAccessRequests,
} from '../../api/accessRequests';
import { logEvent } from '../../lib/telemetry';
import { openRequestCount } from '../../lib/openRequestCount';
import { relativeTimeFrom } from '../../lib/relativeTime';
import { getInitials } from '../../lib/initials';
import type { RoleOption } from './components/roleEditorLogic';
import {
  buildRows, defaultRoleFor, filterRows, pageCount, pageOf, roleLabel, rolesForProduct, summariseByProduct, waitingLine,
  type RequestRow,
} from './requestsLogic';

const pageData = {
  title: 'Requests',
  subtitle: 'People in your organisation asking for a role in an application it already has.',
};

const NO_MANAGE_PERMISSION = "You don't have permission to manage users.";
const NO_ROLES = 'No roles for this application in WorkOS yet.';
const ROLES_UNAVAILABLE = "Roles couldn't be loaded from WorkOS.";
const ROLES_UNAVAILABLE_NOTICE = "Couldn't load the roles WorkOS offers, so Give is unavailable. Reload to try again.";

const GRANTED = 'cloudhouse.enterprise.access_request.granted';
const DISMISSED = 'cloudhouse.enterprise.access_request.dismissed';
const GRANTED_ALL = 'cloudhouse.enterprise.access_request.granted_all';
const DISMISSED_ALL = 'cloudhouse.enterprise.access_request.dismissed_all';
const ACTION_FAILED = 'cloudhouse.enterprise.access_request.request_failed';

type LoadError = 'forbidden' | 'failed' | null;
type PendingBulk = { kind: 'grant' | 'dismiss'; productId: string; productName: string; roleSlug: string | null; count: number };
type ActionResult = { data?: unknown; error?: unknown; response?: Response };

const REASON_MAX = 280;
const DISMISS_FOOTER = 'Dismissing tells the person in the hub and by email, with your reason if you give one.';

function people(n: number): string { return `${n} ${n === 1 ? 'person' : 'people'}`; }
function requestsWord(n: number): string { return `${n} ${n === 1 ? 'request' : 'requests'}`; }
function firstName(name: string): string { return name.trim().split(' ')[0] || name; }

// The optional line the member reads, as the admin typed it; the service only strips what spoofs display.
function ReasonField({ value, onChange, who, disabled }: Readonly<{ value: string; onChange: (v: string) => void; who: string; disabled: boolean }>) {
  return (
    <Textarea
      label={<>Reason <Text span fw={400} c="var(--text-subtle)">(optional)</Text></>}
      description={`${who} sees this exactly as you write it.`}
      inputWrapperOrder={['label', 'input', 'description']}
      value={value} onChange={(e) => onChange(e.currentTarget.value)} maxLength={REASON_MAX} rows={3} style={{ resize: 'none' }}
      disabled={disabled} data-input-cta
      rightSection={<Text fz="xs" c="var(--text-subtle)" pr={8} style={{ whiteSpace: 'nowrap' }}>{value.length} / {REASON_MAX}</Text>}
      rightSectionWidth={70} rightSectionProps={{ style: { alignItems: 'flex-end', paddingBottom: 8 } }}
    />
  );
}
function errorCode(error: unknown): string | undefined { return (error as { error?: string } | undefined)?.error; }

// Plain module fn (outside render) so the clock read stays lint-pure, as trialDaysLeft does.
function nowForRender(): Date {
  return new Date();
}

// "Give · Analyst ▾": the button acts with the chosen role, the chevron changes it.
function GiveControl({ label, productId, roles, value, onChange, onGive, disabled, disabledReason, busy, who }: Readonly<{
  label: string;
  productId: string;
  roles: RoleOption[];
  value: string | null;
  onChange: (slug: string) => void;
  onGive: () => void;
  disabled: boolean;
  disabledReason?: string;
  busy: boolean;
  who: string;
}>) {
  const noRoles = roles.length === 0;
  const reason = disabledReason ?? (noRoles ? NO_ROLES : undefined);
  return (
    <Tooltip label={reason ?? ''} disabled={!reason}>
      <Button.Group>
        <Button size="compact-sm" variant="light" loading={busy} disabled={disabled || noRoles || !value} onClick={onGive}
          styles={{ root: { '--button-color': 'var(--accent-text)' } }}>
          {value ? `${label} · ${roleLabel(productId, value)}` : label}
        </Button>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="light" size={26} disabled={disabled || noRoles} aria-label={`Choose the role to give ${who}`}
              style={{ '--ai-color': 'var(--accent-text)' }}>
              <IconChevronDown size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {roles.map((r) => (
              <Menu.Item key={r.slug} onClick={() => onChange(r.slug)}
                rightSection={r.slug === value ? <IconCheck size={14} /> : null}>
                {roleLabel(productId, r.slug)}
                {r.name && r.name.toLowerCase() !== roleLabel(productId, r.slug).toLowerCase() ? ` — ${r.name}` : ''}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    </Tooltip>
  );
}

export function ProfileRequests() {
  const auth = useAuth();
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  const [rolesUnavailable, setRolesUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<LoadError>(null);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowRole, setRowRole] = useState<Record<string, string>>({});
  const [cardRole, setCardRole] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingBulk, setPendingBulk] = useState<PendingBulk | null>(null);
  const [pendingDismiss, setPendingDismiss] = useState<RequestRow | null>(null);
  const [reason, setReason] = useState('');

  const canManage = (auth.permissions ?? []).includes('org-admin:user-write');

  // The sidebar badge follows the committed list, whichever action changed it.
  useEffect(() => {
    if (!loading && !loadError) openRequestCount.set(rows.length);
  }, [rows, loading, loadError]);

  function dropRow(id: string) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  // Row actions share one shell: guard, clear banners, call, surface the outcome.
  async function runRowAction(
    row: RequestRow,
    action: 'grant' | 'dismiss',
    call: (token: string) => Promise<ActionResult>,
    onSuccess: () => void,
  ) {
    if (pendingId) return;
    setActionError(null);
    setActionNotice(null);
    setPendingId(row.id);
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(adminActionErrorText(undefined, undefined)); return; }
      const { data, error, response } = await call(token);
      if (error || !data) {
        const code = errorCode(error);
        logEvent(ACTION_FAILED, 'Access request action refused', { action, product_id: row.productId, status: response?.status ?? 0, code }, 'WARN');
        setActionError(adminActionErrorText(response?.status, code));
        if (code === 'not_open' || code === 'not_found') dropRow(row.id);
        return;
      }
      onSuccess();
    } catch {
      logEvent(ACTION_FAILED, 'Access request action refused', { action, product_id: row.productId, status: 0, code: undefined }, 'WARN');
      setActionError(adminActionErrorText(undefined, undefined));
    } finally {
      setPendingId(null);
    }
  }

  function give(row: RequestRow) {
    const slug = roleForRow(row);
    if (!slug) return;
    return runRowAction(row, 'grant', (token) => grantAccessRequest(token, row.id, slug), () => {
      logEvent(GRANTED, 'Admin gave a requested role', { request_id: row.id, product_id: row.productId, role_slug: slug });
      dropRow(row.id);
      setActionNotice(`${roleLabel(row.productId, slug)} role for ${row.productName} given to ${row.name}.`);
    });
  }

  async function dismiss(row: RequestRow, why: string) {
    setPendingDismiss(null);
    setReason('');
    await runRowAction(row, 'dismiss', (token) => dismissAccessRequest(token, row.id, why), () => {
      logEvent(DISMISSED, 'Admin dismissed an access request', { request_id: row.id, product_id: row.productId });
      dropRow(row.id);
      setActionNotice(`Request from ${row.name} dismissed.`);
    });
  }

  async function runBulk(pending: PendingBulk) {
    setBulkBusy(true);
    setActionError(null);
    setActionNotice(null);
    const action = pending.kind === 'grant' ? 'grant_all' : 'dismiss_all';
    const refused = (status: number | undefined, code: string | undefined) => {
      logEvent(ACTION_FAILED, 'Access request action refused', { action, product_id: pending.productId, status: status ?? 0, code }, 'WARN');
      setActionError(adminActionErrorText(status, code));
    };
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(adminActionErrorText(undefined, undefined)); return; }
      if (pending.kind === 'grant') {
        if (!pending.roleSlug) return;
        const { data, error, response } = await grantAllAccessRequests(token, pending.productId, pending.roleSlug);
        if (error || !data) { refused(response?.status, errorCode(error)); return; }
        const granted = Number(data.granted);
        logEvent(GRANTED_ALL, 'Admin gave a role to everyone waiting', { product_id: pending.productId, role_slug: pending.roleSlug, granted, failed: data.failed.length });
        setActionNotice(`Access to ${pending.productName} given to ${people(granted)}.`
          + (data.failed.length > 0 ? ` ${data.failed.length} could not be given — they are still listed.` : ''));
      } else {
        const { data, error, response } = await dismissAllAccessRequests(token, pending.productId, reason);
        if (error || !data) { refused(response?.status, errorCode(error)); return; }
        const dismissed = Number(data.dismissed);
        logEvent(DISMISSED_ALL, 'Admin dismissed every request for a product', { product_id: pending.productId, dismissed });
        setActionNotice(`${requestsWord(dismissed)} for ${pending.productName} dismissed.`);
      }
      try { await load(); } catch { setActionError('Done, but the list could not refresh — reload to see it.'); }
    } catch {
      refused(0, undefined);
    } finally {
      setBulkBusy(false);
      setPendingBulk(null);
      setReason('');
    }
  }

  async function load(signal?: AbortSignal): Promise<void> {
    const token = await auth.getAccessToken();
    if (!token) { setLoadError('failed'); return; }
    const [requests, users, roles] = await Promise.all([
      listAccessRequests(token, signal),
      api.GET('/users', { headers: bearer(token), signal }),
      api.GET('/users/roles', { headers: bearer(token), signal }),
    ]);
    if (requests.error || !requests.data) {
      setLoadError(requests.response?.status === 403 ? 'forbidden' : 'failed');
      return;
    }
    setLoadError(null);
    setRolesUnavailable(Boolean(roles.error) || !roles.data);
    setAvailableRoles(roles.data?.roles ?? []);
    setRows(buildRows(requests.data.requests, users.data?.users ?? []));
  }

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try { await load(ctrl.signal); }
      catch (e) { if ((e as Error).name !== 'AbortError') setLoadError('failed'); }
      finally { setLoading(false); }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const summaries = summariseByProduct(rows);
  const filtered = filterRows(rows, search, productFilter);
  const pages = pageCount(filtered.length);
  const currentPage = Math.min(page, pages);
  const visible = pageOf(filtered, currentPage);
  const now = nowForRender();

  const rolesOf = (productId: string) => rolesForProduct(availableRoles, productId);
  const roleForRow = (row: RequestRow) => rowRole[row.id] ?? defaultRoleFor(row.productId, rolesOf(row.productId));
  const roleForCard = (productId: string) => cardRole[productId] ?? defaultRoleFor(productId, rolesOf(productId));
  const noPerm = canManage ? undefined : NO_MANAGE_PERMISSION;
  const giveReason = noPerm ?? (rolesUnavailable ? ROLES_UNAVAILABLE : undefined);

  return (
    <Stack flex={1} style={{ overflowY: 'auto' }} px={32} py={24} gap={0}>
      <SectionTitle {...pageData} />

      {loading && <Center py={20}><Loader size="sm" /></Center>}
      {loadError === 'forbidden' && <InlineNotice tone="error" message="You don't have permission to see requests." />}
      {loadError === 'failed' && (
        <InlineNotice tone="error" message="Couldn't load requests from the entitlements service — check it is running." />
      )}
      {!loadError && rolesUnavailable && <InlineNotice tone="error" message={ROLES_UNAVAILABLE_NOTICE} />}
      {actionError && <InlineNotice tone="error" message={actionError} onClose={() => setActionError(null)} />}
      {actionNotice && <InlineNotice tone="success" message={actionNotice} onClose={() => setActionNotice(null)} />}

      {summaries.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" mt="lg">
          {summaries.map((s) => (
            <Stack key={s.productId} gap={10} p={16} data-testid={`requests-card-${s.productId}`}
              style={{ border: '1px solid var(--border-default)', borderRadius: 12, background: 'var(--bg-surface)' }}>
              <Group gap={10} wrap="nowrap">
                {s.logo && <img src={s.logo} alt="" style={{ width: 28, height: 28 }} />}
                <Text fw={700} fz="sm">{s.productName}</Text>
              </Group>
              <Text fz="xs" c="var(--text-muted)">{waitingLine(s.count, s.oldestRequestedAt, now)}</Text>
              <Group gap={8}>
                <GiveControl label="Give everyone" productId={s.productId} roles={rolesOf(s.productId)}
                  value={roleForCard(s.productId)} onChange={(slug) => setCardRole((m) => ({ ...m, [s.productId]: slug }))}
                  onGive={() => setPendingBulk({ kind: 'grant', productId: s.productId, productName: s.productName, roleSlug: roleForCard(s.productId), count: s.count })}
                  disabled={!canManage || bulkBusy || pendingId !== null} disabledReason={giveReason} busy={false}
                  who={`everyone waiting for ${s.productName}`} />
                <Tooltip label={noPerm ?? ''} disabled={!noPerm}>
                  <Button size="compact-sm" variant="default" disabled={!canManage || bulkBusy || pendingId !== null}
                    onClick={() => setPendingBulk({ kind: 'dismiss', productId: s.productId, productName: s.productName, roleSlug: null, count: s.count })}>
                    Dismiss all
                  </Button>
                </Tooltip>
              </Group>
            </Stack>
          ))}
        </SimpleGrid>
      )}

      <Group gap={10} wrap="wrap" mt="lg" mb="md">
        <TextInput placeholder="Search people…" aria-label="Search people" value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
          leftSection={<IconSearch size={15} color="var(--text-subtle)" />} w={280} data-input-cta />
        <Select aria-label="Filter by application" value={productFilter} allowDeselect={false} w={200} data-input-cta
          onChange={(value) => { setProductFilter(value ?? 'all'); setPage(1); }}
          data={[{ value: 'all', label: 'All applications' }, ...summaries.map((s) => ({ value: s.productId, label: s.productName }))]} />
      </Group>

      <Box style={{ border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)', flex: 'none' }}>
        <Table highlightOnHover={visible.length > 0} style={{ '--table-vertical-spacing': '14px 18px', '--table-border-color': 'var(--border-default)', '--table-hover-color': 'var(--bg-subtle)' }}>
          <Table.Thead>
            <Table.Tr style={{ '--table-vertical-spacing': '11px 18px', color: 'var(--text-subtle)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', background: 'var(--bg-subtle)' }}>
              <Table.Th>Person</Table.Th>
              <Table.Th>Application</Table.Th>
              <Table.Th>Requested</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!loading && !loadError && rows.length === 0 && (
              <Table.Tr><Table.Td colSpan={4}>
                <Stack align="center" gap={6} py={40}><Text fz="sm" fw={600}>No one is waiting for access.</Text></Stack>
              </Table.Td></Table.Tr>
            )}
            {!loading && rows.length > 0 && filtered.length === 0 && (
              <Table.Tr><Table.Td colSpan={4}>
                <Stack align="center" gap={6} py={40}><Text fz="sm" fw={600}>No requests match your search.</Text></Stack>
              </Table.Td></Table.Tr>
            )}
            {visible.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td>
                  <Group gap={11} wrap="nowrap">
                    <Avatar size={32} radius="xl" color="white" lh="normal"
                      style={{ '--avatar-bg': 'linear-gradient(135deg, rgb(81, 100, 112), rgb(13, 40, 57))', '--avatar-bd': 'none' }}>
                      {getInitials(row.name)}
                    </Avatar>
                    <Stack gap={0}>
                      <Text fz="sm" fw={600} lh="normal">{row.name}</Text>
                      <Text fz="xs" fw={500} c="var(--text-subtle)" lh="normal">{row.email}</Text>
                    </Stack>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={8} wrap="nowrap">
                    {row.logo && <img src={row.logo} alt="" style={{ width: 20, height: 20 }} />}
                    <Text fz="sm">{row.productName}</Text>
                  </Group>
                </Table.Td>
                <Table.Td><Text fz="sm">{relativeTimeFrom(row.requestedAt, now)}</Text></Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <GiveControl label="Give" productId={row.productId} roles={rolesOf(row.productId)}
                      value={roleForRow(row)} onChange={(slug) => setRowRole((m) => ({ ...m, [row.id]: slug }))}
                      onGive={() => void give(row)} disabled={!canManage || pendingId !== null || bulkBusy} disabledReason={giveReason}
                      busy={pendingId === row.id} who={row.name} />
                    <Tooltip label={noPerm ?? ''} disabled={!noPerm}>
                      <Button size="compact-sm" variant="default" disabled={!canManage || pendingId !== null || bulkBusy} onClick={() => setPendingDismiss(row)}>
                        Dismiss
                      </Button>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      {pages > 1 && (
        <Group justify="flex-end" mt="md">
          <Pagination total={pages} value={currentPage} onChange={setPage} size="sm" />
        </Group>
      )}

      <Text fz="xs" c="var(--text-subtle)" mt="md">{DISMISS_FOOTER}</Text>

      <Modal opened={pendingDismiss !== null} onClose={() => { setPendingDismiss(null); setReason(''); }} centered
        title={pendingDismiss ? `Dismiss ${pendingDismiss.name}'s request for ${pendingDismiss.productName}?` : ''}>
        {pendingDismiss && (
          <Stack gap="md">
            <Text fz="sm">{firstName(pendingDismiss.name)} will be told in the hub and by email. Their card goes back to Request access.</Text>
            <ReasonField value={reason} onChange={setReason} who={firstName(pendingDismiss.name)} disabled={false} />
            <Group justify="flex-end" gap={10}>
              <Button variant="default" onClick={() => { setPendingDismiss(null); setReason(''); }}>Cancel</Button>
              <Button color="red" onClick={() => void dismiss(pendingDismiss, reason)}>Dismiss</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={pendingBulk !== null} onClose={() => { if (!bulkBusy) { setPendingBulk(null); setReason(''); } }} centered
        title={pendingBulk ? (pendingBulk.kind === 'grant'
          ? `Give everyone access to ${pendingBulk.productName}?`
          : `Dismiss all requests for ${pendingBulk.productName}?`) : ''}>
        {pendingBulk && (
          <Stack gap="md">
            <Text fz="sm">
              {pendingBulk.kind === 'grant'
                ? `${people(pendingBulk.count)} get the ${roleLabel(pendingBulk.productId, pendingBulk.roleSlug ?? '')} role for ${pendingBulk.productName}. Anyone who already holds a role keeps it.`
                : `${requestsWord(pendingBulk.count)} are dismissed. Each person is told in the hub and by email, with your reason if you give one.`}
            </Text>
            {pendingBulk.kind === 'dismiss' && <ReasonField value={reason} onChange={setReason} who="Each person" disabled={bulkBusy} />}
            <Group justify="flex-end" gap={10}>
              <Button variant="default" disabled={bulkBusy} onClick={() => { setPendingBulk(null); setReason(''); }}>Cancel</Button>
              <Button loading={bulkBusy} color={pendingBulk.kind === 'dismiss' ? 'red' : undefined} onClick={() => void runBulk(pendingBulk)}>
                {pendingBulk.kind === 'grant' ? 'Give everyone' : 'Dismiss all'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
